// Canvas-based export module using fabric.js for pixel-perfect rendering
// This replaces html2canvas with a more reliable approach

// Initialize Supabase client
const { createClient } = supabase;
const supabaseClient = createClient(
    'https://bmqexmsqwxfzejjkkevb.supabase.co',
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJtcWV4bXNxd3hmemVqamtrZXZiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDkxNTc2NjUsImV4cCI6MjA2NDczMzY2NX0.BhtmZtQ4NT5C8q4H3n5_uWn3dOMddIRWEiG2kFI7vbg'
);

// Supabase helper functions
async function uploadImage(blob, path) {
    const { data, error } = await supabaseClient
        .storage
        .from('posters')
        .upload(path, blob, {
            cacheControl: '3600',
            upsert: true
        });

    if (error) {
        console.error('Upload error:', error);
        return null;
    }

    const { data: publicUrlData } = supabaseClient
        .storage
        .from('posters')
        .getPublicUrl(path);

    return publicUrlData.publicUrl;
}

async function saveDesign(fullresUrl, mapConfigJson) {
    // Debug log to check payload and type
    console.log('Payload to insert:', {
        fullres_url: fullresUrl,
        map_config: mapConfigJson,
        map_config_type: typeof mapConfigJson
    });
    const { data, error } = await supabaseClient
        .from('map_designs')
        .insert([{
            preview_url: null, // Keep column for future use but set to null
            fullres_url: fullresUrl,
            map_config: mapConfigJson
        }]);

    if (error) {
        console.error('Insert error:', error);
        return null;
    }
    
    return data;
}

async function convertPngToJpg(pngBlob) {
    const TARGET_WIDTH = 300;
    const JPEG_QUALITY = 0.3;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                // Calculate proportional height
                const scale = TARGET_WIDTH / img.width;
                const targetHeight = img.height * scale;

                const canvas = document.createElement('canvas');
                canvas.width = TARGET_WIDTH;
                canvas.height = targetHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                canvas.toBlob(blob => {
                    if (!blob) return reject("Failed to create JPG blob");
                    resolve(blob);
                }, 'image/jpeg', JPEG_QUALITY);
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(pngBlob);
    });
}

class CanvasExporter {
    constructor() {
        this.fontFamilies = {
            'Font 1': 'Pricedown',
            'Font 2': 'Respondent',
            'Font 3': 'SignPainter',
            'Font 4': 'ChaletComprime',
            'Font 5': 'ChaletLondon',
            'Font 6': 'Legal',
            'Font 7': 'Legal3D'
        };
        
        // Shadow color mappings
        this.shadowColors = {
            'green': 'rgba(0, 255, 0, 0.8)',
            'purple': 'rgba(128, 0, 255, 0.8)',
            'blue': 'rgba(0, 102, 255, 0.8)',
            'orange': 'rgba(255, 128, 0, 0.8)',
            'pink': 'rgba(255, 0, 128, 0.8)',
            'yellow': 'rgba(255, 204, 0, 0.8)',
            'red': 'rgba(255, 0, 0, 0.8)',
            'white': 'rgba(255, 255, 255, 0.8)'
        };
    }

    async loadFonts() {
        // Ensure all custom fonts are loaded
        await document.fonts.ready;
        
        // Force load specific fonts
        const fontPromises = Object.values(this.fontFamilies).map(font => {
            return document.fonts.load(`bold 48px ${font}`).catch(() => {
                console.warn(`Font ${font} could not be loaded`);
            });
        });
        
        await Promise.all(fontPromises);
    }

    getTextStyle(elementId, isTitle = true) {
        const fontSelect = document.getElementById(isTitle ? 'title-font' : 'subtitle-font');
        const fontSizeInput = document.getElementById(isTitle ? 'title-font-size' : 'subtitle-font-size');
        const borderSizeInput = document.getElementById(isTitle ? 'title-border-size' : 'subtitle-border-size');
        const shadowBlurInput = document.getElementById(isTitle ? 'title-shadow-blur' : 'subtitle-shadow-blur');
        const shadowColorEl = document.querySelector(`#${isTitle ? 'title' : 'subtitle'}-shadow-colors .color-option.active`);
        
        const fontFamily = this.fontFamilies[fontSelect.value] || 'Arial';
        const fontSize = parseFloat(fontSizeInput.value) * (isTitle ? 24 : 16); // Base size multiplier
        const borderSize = parseFloat(borderSizeInput.value);
        const shadowBlur = parseFloat(shadowBlurInput.value);
        const shadowColor = this.shadowColors[shadowColorEl?.dataset.color || 'purple'];
        
        return {
            fontFamily,
            fontSize,
            borderSize,
            shadowBlur,
            shadowColor
        };
    }

    async captureMapCanvas(map, width, height) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
          const mapCanvas = map.getCanvas();

          // Use the export size requested by the caller (matches your Landscape/Portrait selection)
          const exportCanvas = document.createElement('canvas');
          exportCanvas.width = width;
          exportCanvas.height = height;
          const ctx = exportCanvas.getContext('2d');

          // Snapshot current map into an Image
          const dataURL = mapCanvas.toDataURL('image/jpeg', 0.95);
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
            img.src = dataURL;
          });

          // Cover-fit crop (like CSS background-size: cover) so the map fills the export exactly
          const srcW = img.width;
          const srcH = img.height;
          const dstW = width;
          const dstH = height;
          const srcRatio = srcW / srcH;
          const dstRatio = dstW / dstH;

          let sx = 0, sy = 0, sWidth = srcW, sHeight = srcH;
          if (srcRatio > dstRatio) {
            // Source wider than destination -> crop left/right
            sHeight = srcH;
            sWidth = Math.round(sHeight * dstRatio);
            sx = Math.round((srcW - sWidth) / 2);
          } else {
            // Source taller than destination -> crop top/bottom
            sWidth = srcW;
            sHeight = Math.round(sWidth / dstRatio);
            sy = Math.round((srcH - sHeight) / 2);
          }

          ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, dstW, dstH);

          // Save crop info so pin placement can mirror this transform
          this.__iosCrop = { sx, sy, sWidth, sHeight, srcW, srcH, dstW, dstH };

          return exportCanvas;
        } else {
            const mapContainer = map.getContainer();
            const originalWidth = mapContainer.style.width;
            const originalHeight = mapContainer.style.height;
            const originalBounds = map.getBounds();

            const style = map.getStyle();
            const previewZoom = map.getZoom();
            const allLayerIds = style.layers.map(l => l.id);
            const allLayerVisibilitySnapshot = {};
            allLayerIds.forEach(id => {
                try {
                    const layer = style.layers.find(l => l.id === id);
                    let visible = true;
                    const layoutVis = map.getLayoutProperty(id, 'visibility');
                    if (layoutVis === 'none') visible = false;
                    if (typeof layer.minzoom !== 'undefined' && previewZoom < layer.minzoom) visible = false;
                    if (typeof layer.maxzoom !== 'undefined' && previewZoom >= layer.maxzoom) visible = false;
                    allLayerVisibilitySnapshot[id] = visible ? 'visible' : 'none';
                } catch (e) {
                    allLayerVisibilitySnapshot[id] = 'visible';
                }
            });

            mapContainer.style.width = width + 'px';
            mapContainer.style.height = height + 'px';
            map.resize();
            map.fitBounds(originalBounds, { animate: false, padding: 0 });

            await new Promise(resolve => {
                function checkRendered() {
                    if (
                        map.loaded() &&
                        map.getContainer().clientWidth === width &&
                        map.getContainer().clientHeight === height
                    ) {
                        resolve();
                    } else {
                        setTimeout(checkRendered, 100);
                    }
                }
                checkRendered();
            });

            allLayerIds.forEach(id => {
                if (map.getLayer(id)) {
                    map.setLayoutProperty(id, 'visibility', allLayerVisibilitySnapshot[id]);
                }
            });

            await new Promise(resolve => {
                function checkRendered() {
                    if (map.loaded()) {
                        resolve();
                    } else {
                        setTimeout(checkRendered, 100);
                    }
                }
                checkRendered();
            });

            const mapCanvas = map.getCanvas();
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(mapCanvas, 0, 0, width, height);

            const textLayers = ['place-labels', 'poi-labels'];
            allLayerIds.forEach(id => {
                if (map.getLayer(id)) {
                    const visibility = textLayers.includes(id) ? 'none' : 'visible';
                    map.setLayoutProperty(id, 'visibility', visibility);
                }
            });

            mapContainer.style.width = originalWidth;
            mapContainer.style.height = originalHeight;
            map.resize();
            map.fitBounds(originalBounds, { animate: false, padding: 0 });

            return canvas;
        }
    }

    createTextWithEffects(text, style, fabric, blurScale = 1) {
        // Create fill-only text
        const fillText = new fabric.Text(text, {
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fill: 'white',
        fontWeight: 'bold',
        textAlign: 'left',
        originX: 'left',
        originY: 'top',
        stroke: null
        });

        // Create stroke-only text (underneath)
        let strokeText = null;
        if (style.borderSize > 0) {
        strokeText = new fabric.Text(text, {
            fontFamily: style.fontFamily,
            fontSize: style.fontSize,
            fill: null,
            stroke: '#000000',
            strokeWidth: style.borderSize * 2,
            fontWeight: 'bold',
            textAlign: 'left',
            originX: 'left',
            originY: 'top'
        });
        }

        // Apply shadow to fill text
        fillText.set('shadow', {
        color: style.shadowColor,
        blur: style.shadowBlur * blurScale,
        offsetX: 0,
        offsetY: 0
        });

        // Return a group: [stroke (under), fill (over)]
        if (strokeText) {
        return new fabric.Group([strokeText, fillText], {
            originX: 'left',
            originY: 'top',
            selectable: false
        });
        } else {
        return fillText;
        }

        // Create shadow effect using fabric.js shadow syntax
        textObj.set('shadow', {
            color: style.shadowColor,
            blur: style.shadowBlur * blurScale,
            offsetX: 0,
            offsetY: 0
        });

        return textObj;
    }

    async exportPoster(options = {}) {
        const {
            width = 3600,
            height = 2250, // 8:5 aspect ratio
            scale = 1
        } = options;

        try {
            await this.loadFonts();

            // Get preview canvas and overlay elements
            const previewCanvas = document.getElementById('preview-canvas');
            const mapTitlePreview = document.getElementById('map-title-preview');
            const mapSubtitlePreview = document.getElementById('map-subtitle-preview');
            const previewRect = previewCanvas.getBoundingClientRect();
            const titleRect = mapTitlePreview.getBoundingClientRect();
            const subtitleRect = mapSubtitlePreview.getBoundingClientRect();
            // Get font sizes in px
            const titleFontPx = parseFloat(window.getComputedStyle(mapTitlePreview).fontSize);
            const subtitleFontPx = parseFloat(window.getComputedStyle(mapSubtitlePreview).fontSize);
            // Get text align
            const titleAlign = window.getComputedStyle(mapTitlePreview).textAlign;
            const subtitleAlign = window.getComputedStyle(mapSubtitlePreview).textAlign;
            // Position presets (must match preview logic)
            const marginPx = 0.03 * previewRect.width;
            const positions = {
                'top-left':       { top: marginPx,  left: marginPx },
                'top-center':     { top: marginPx,  left: previewRect.width / 2,  tx: -50 },
                'top-right':      { top: marginPx,  right: marginPx },
                'center-left':    { top: previewRect.height / 2, left: marginPx,            ty: -50 },
                'center':         { top: previewRect.height / 2, left: previewRect.width / 2, tx: -50, ty: -50 },
                'center-right':   { top: previewRect.height / 2, right: marginPx,           ty: -50 },
                'bottom-left':    { bottom: marginPx, left: marginPx },
                'bottom-center':  { bottom: marginPx, left: previewRect.width / 2, tx: -50 },
                'bottom-right':   { bottom: marginPx, right: marginPx }
            };

            // Get selected positions from controls
            const titleKey = document.getElementById('title-position').value;
            const subtitleMode = document.getElementById('subtitle-position').value;
            const subtitleKey = subtitleMode === 'separate'
                ? document.getElementById('subtitle-separate-position-select').value
                : titleKey;
            const pushDown = (subtitleMode === 'separate' && titleKey === subtitleKey);
            const gapPx = 0.02 * previewRect.height; // 2% of preview height as gap

            // Helper to get anchor/transform for a given key
            function getOverlayExportProps(key, isTitle, pushDown) {
                const p = positions[key];
                let left = 0, top = 0, originX = 'left', originY = 'top', transforms = [];
                if (p.left !== undefined) {
                    left = (typeof p.left === 'number' ? p.left : parseFloat(p.left));
                    originX = (key.endsWith('center')) ? 'center' : (key.endsWith('right') ? 'right' : 'left');
                } else if (p.right !== undefined) {
                    left = previewRect.width - p.right;
                    originX = 'right';
                }
                if (p.top !== undefined) {
                    top = (typeof p.top === 'number' ? p.top : parseFloat(p.top));
                    originY = 'top';
                } else if (p.bottom !== undefined) {
                    top = previewRect.height - p.bottom;
                    originY = 'bottom';
                }
                if (p.tx) transforms.push(`translateX(${p.tx}%)`);
                if (p.ty) transforms.push(`translateY(${p.ty}%)`);
                if (pushDown && !isTitle) transforms.push('translateY(100%)');
                return { left, top, originX, originY, transforms: transforms.join(' ') };
            }

            // Get export props for title and subtitle
            const titleProps = getOverlayExportProps(titleKey, true, false);
            const subtitleProps = getOverlayExportProps(subtitleKey, false, pushDown);

            // Convert preview px to export px
            function pxPreviewToExport(px, axis) {
                return axis === 'x'
                    ? (px / previewRect.width) * width
                    : (px / previewRect.height) * height;
            }

            // Create fabric canvas
            const canvas = new fabric.Canvas(null, {
                width: width,
                height: height,
                backgroundColor: '#262626'
            });

            // 1. Add map background
            if (!window.map) {
                throw new Error('Map instance not found');
            }
            const mapCanvas = await this.captureMapCanvas(window.map, width, height);
            const mapImage = new fabric.Image(mapCanvas, {
                left: 0,
                top: 0,
                selectable: false
            });
            canvas.add(mapImage);

            // Calculate blur scale factor
            const blurScale = height / previewRect.height;

            // 2. Add title text at proportional position/size
            const titleText = document.getElementById('map-title').value;
            const titleStyle = this.getTextStyle('title', true);
            titleStyle.fontSize = pxPreviewToExport(titleFontPx, 'y');
            const title = this.createTextWithEffects(titleText, titleStyle, fabric, blurScale);
            let titleTop = pxPreviewToExport(titleProps.top, 'y');
            // let topY = pxPreviewToExport(titleProps.top, 'y');

            // Apply default or custom margin if title is at top or bottom
            // if (titleKey.startsWith('top')) {
            //     topY += pxPreviewToExport(titleMargin, 'y');
            // } else if (titleKey.startsWith('bottom')) {
            //     topY += pxPreviewToExport(titleBottomOffset, 'y');
            // }
            
            // const bottomOffset = pxPreviewToExport(titleBottomOffset, 'y');
            // Calculate title Y position (respect titleMargin)
            // let titleTop = pxPreviewToExport(titleProps.top, 'y');
            // if (titleKey.startsWith('top')) {
            //     titleTop += pxPreviewToExport(titleMargin, 'y');
            // } else if (titleKey.startsWith('bottom')) {
            //     titleTop -= pxPreviewToExport(titleBottomOffset + titleMargin, 'y');
            // }

            // Calculate subtitle Y position separately — no titleMargin!
            let subtitleTop = pxPreviewToExport(subtitleProps.top ?? 0, 'y');

            // Fix for separate mode: if title and subtitle use the same position
            if (subtitleMode === 'separate' && titleKey === subtitleKey) {
                console.warn("⚠️ Title and Subtitle have the same position in 'separate' mode. Adjusting subtitle downward.");
                subtitleTop += pxPreviewToExport(100, 'y'); // Push subtitle down by 100px
            }
            
            title.set({
              left: pxPreviewToExport(titleProps.left, 'x'),
              top: titleTop,
              fontSize: pxPreviewToExport(titleFontPx, 'y'),
              originX: titleProps.originX,
              originY: titleProps.originY
            });

            if (titleProps.transforms) {
                const tx = /translateX\((-?\d+)%\)/.exec(titleProps.transforms);
                const ty = /translateY\((-?\d+)%\)/.exec(titleProps.transforms);
                if (ty) title.set({ top: title.top + (parseFloat(ty[1]) / 100) * title.height });
            }
            canvas.add(title);

            // 3. Add subtitle text at proportional position/size
            const subtitleText = document.getElementById('map-subtitle').value;
            const subtitleStyle = this.getTextStyle('subtitle', false);
            subtitleStyle.fontSize = pxPreviewToExport(subtitleFontPx, 'y');
            const subtitle = this.createTextWithEffects(subtitleText, subtitleStyle, fabric, blurScale);

            const isSubtitleCenter = ['top-center', 'bottom-center', 'center'].includes(subtitleKey);

            // --- FIX: keep subtitle under title for top-* when mode is with/below (match preview) ---
            (function fixTopStacking() {
            try {

                const isWithOrBelow = (subtitleMode === 'with-title' || subtitleMode === 'below-title');
                const isTopTitle   = (typeof titleKey === 'string' && titleKey.startsWith('top'));
                if (!isWithOrBelow || !isTopTitle || !title) return;

                // convert the preview-based gap to export pixels
                const scaleY = height / previewRect.height;
                const gapExport = gapPx * scaleY; // gapPx exists: 0.02 * previewRect.height

                // Fabric's actual rendered height for the title
                const tH = (typeof title.getScaledHeight === 'function')
                ? title.getScaledHeight()
                : (title.height || 0);

                // place subtitle directly under the title (export coordinates)
                subtitleTop = (title.top || 0) + tH + gapExport;
            } catch (e) {
                // don't crash export if anything goes wrong here
                console.warn('Top stacking patch skipped:', e);
            }
            })();

            subtitle.set({
                left: pxPreviewToExport(subtitleProps.left, 'x'),
                top: subtitleTop,
                fontSize: pxPreviewToExport(subtitleFontPx, 'y'),
                originX: isSubtitleCenter ? 'center' : subtitleProps.originX,
                originY: subtitleProps.originY,
                angle: 0
            });
            
            if (subtitleProps.transforms && !pushDown) {
                const ty = /translateY\((-?\d+)%\)/.exec(subtitleProps.transforms);
                if (ty) {
                    subtitle.set({ top: subtitle.top + (parseFloat(ty[1]) / 100) * subtitle.height });
                }
            }

            // --- PORTRAIT-ONLY: make TOP/CENTER subtitle Y match preview + a little extra space ---
            if (window.currentOrientation === 'portrait') {
            const isWithOrBelow = (subtitleMode === 'below-title' || subtitleMode === 'with-title');
            // Only adjust when the title is NOT bottom-aligned (top/center variants)
            if (isWithOrBelow && !titleKey.startsWith('bottom')) {
                // Absolute subtitle top from the preview DOM + small cushion → scale to export pixels
                const portraitTopExtraGapPreview = 8; // increase to 10/12 if you want a bigger gap
                const subtitleTopAbs = pxPreviewToExport(
                (subtitleRect.top - previewRect.top) + portraitTopExtraGapPreview,
                'y'
                );

                subtitle.set({
                originY: 'top',
                top: subtitleTopAbs
                });
                if (typeof subtitle.setCoords === 'function') subtitle.setCoords();
            }
            }

            // --- Margin logic for 'With Title' (was 'below-title') ---
            // const isBottomAligned = ['bottom-left', 'bottom-center', 'bottom-right'].includes(titleKey);
            // const isWithOrBelow = subtitleMode === 'below-title' || subtitleMode === 'with-title';
            
            // if (isWithOrBelow) {
            //     const gap = pxPreviewToExport(subtitleMargin, 'y');
            
            //     const newSubtitleTop = title.top + title.height + gap;
            //     subtitle.set({ top: newSubtitleTop });
            // }
            // === Fix bottom-aligned title/subtitle positions ===
            // === Fix bottom-aligned title/subtitle positions (responsive & correct) ===
            const isWithOrBelow = subtitleMode === 'below-title' || subtitleMode === 'with-title';
                if (isWithOrBelow) {
                    // detect small preview (mobile-like)
                    const isPreviewSmall = previewRect.width <= 600 || previewRect.height <= 700 || window.innerWidth <= 600;

                    // base bottom padding = preview marginPx scaled to export px (keeps preview/export consistent)
                    const bottomPaddingTitle = pxPreviewToExport(marginPx, 'y') * (isPreviewSmall ? 0.8 : 1);

                    // small extra lift (use subtitleMargin if set), scaled down on small previews
                    const extraLiftPreview = Math.max(5, subtitleMargin || 5);
                    const extraLiftSubtitle = pxPreviewToExport(extraLiftPreview, 'y') * (isPreviewSmall ? 0.6 : 1);

                    if (titleKey.startsWith('bottom')) {
                    // Small extra lift for title to avoid overlap
                    const titleLift = pxPreviewToExport(window.innerWidth > 600 ? 25 : 10, 'y') * (isPreviewSmall ? 0.6 : 1);

                    title.set({
                        originY: 'bottom',
                        top: height - (bottomPaddingTitle + extraLiftSubtitle + titleLift)
                    });

                    // Subtitle anchored from bottom, unchanged
                    subtitle.set({
                        originY: 'bottom',
                        top: height - bottomPaddingTitle
                    });
                }

            }

            canvas.add(subtitle);

            // 4. Add pin markers on the map (behind attractions list)
            await this.addPinMarkers(canvas, width, height, fabric);

            // 5. Add attractions/pins list at proportional position/size (on top of pins)
            const pinsListPreview = document.getElementById('pins-list-preview');
            if (pinsListPreview) {
                const pinsRect = pinsListPreview.getBoundingClientRect();
                const pinsLeftPct = (pinsRect.left - previewRect.left) / previewRect.width;
                const pinsTopPct = (pinsRect.top - previewRect.top) / previewRect.height;
                const pinsWidthPct = pinsRect.width / previewRect.width;
                const pinsHeightPct = pinsRect.height / previewRect.height;
                await this.addPinsList(canvas, width, height, fabric, {
                    leftPct: pinsLeftPct,
                    topPct: pinsTopPct,
                    widthPct: pinsWidthPct,
                    heightPct: pinsHeightPct
                });
            } else {
                await this.addPinsList(canvas, width, height, fabric);
            }

            // Render canvas and export
            canvas.renderAll();
            return new Promise((resolve, reject) => {
                try {
                    const dataURL = canvas.toDataURL({
                        format: 'png',
                        quality: 1.0,
                        multiplier: 1
                    });
                    fetch(dataURL)
                        .then(res => res.blob())
                        .then(blob => resolve(blob))
                        .catch(err => reject(err));
                } catch (error) {
                    reject(error);
                }
            });
        } catch (error) {
            console.error('Export error:', error);
            throw error;
        }
    }

    positionElement(element, position, canvasWidth, canvasHeight, padding) {
        switch (position) {
            case 'top-left':
                element.set({ left: padding, top: padding });
                break;
            case 'top-center':
                element.set({ left: canvasWidth / 2, top: padding, originX: 'center' });
                break;
            case 'top-right':
                element.set({ left: canvasWidth - padding, top: padding, originX: 'right' });
                break;
            case 'bottom-left':
                element.set({ left: padding, top: canvasHeight - padding - element.height, originY: 'bottom' });
                break;
            case 'bottom-center':
                element.set({ left: canvasWidth / 2, top: canvasHeight - padding - element.height, originX: 'center', originY: 'bottom' });
                break;
            case 'bottom-right':
                element.set({ left: canvasWidth - padding, top: canvasHeight - padding - element.height, originX: 'right', originY: 'bottom' });
                break;
        }
    }

    async addPinsList(canvas, width, height, fabric, options = {}) {
        const pinsData = window.markers || [];
        
        console.log('Adding pins list - pins count:', pinsData.length);
        console.log('Pins list data:', pinsData.map(p => ({ name: p.name })));
        
        if (pinsData.length === 0) return;

        // --- sizing for attractions list (portrait normalized to match mobile landscape) ---
        const isPortrait = (window.currentOrientation === 'portrait');

        // Your export heights: landscape = 2250, portrait = 3600.
        // Scale portrait sizes by 2250/3600 ≈ 0.625 so they look the same size as landscape.
        const PORTRAIT_NORMALIZE = 2250 / 3600;
        const scale = isPortrait ? PORTRAIT_NORMALIZE : 1;

        const paddingX = width  * 0.012;                 // keep X padding rule the same
        const paddingY = (height * 0.003) * scale;       // normalize portrait vertical padding
        const gapY     = (height * 0.004) * scale;       // normalize portrait row gap

        // Icon & text normalized so portrait == landscape visual size
        const iconSize = (height * 0.022 * 1.2) * scale;
        const fontSize = (height * 0.016 * 1.2) * scale;

        let currentY   = height * 0.15;                  // leave start offset as-is
        const rightX   = width - (width * 0.03);         // right padding as-is

        for (const pin of pinsData) {
            // 1. Create text object to measure width
            const pinText = new fabric.Text(pin.name, {
                fontFamily: 'Chalet',
                fontSize: fontSize,
                fill: 'white',
                fontWeight: 'normal',
                originX: 'left',
                originY: 'center',
            });
            // 2. Calculate total width: text + icon + margin + padding
            const textWidth = pinText.width;
            const iconMargin = iconSize * 0.25; // space between text and icon
            const totalWidth = textWidth + iconSize + iconMargin + (2 * paddingX);
            const rowHeight = (Math.max(pinText.height, iconSize) + 2 * paddingY)*1.0; // reduced height multiplier

            // 3. Draw background rectangle (right-aligned)
            const bgRect = new fabric.Rect({
                left: rightX,
                top: currentY,
                width: totalWidth,
                height: rowHeight,
                fill: 'rgba(0, 0, 0, 0.8)', // Reduced opacity to 0.8
                rx: 0,
                ry: 0,
                originX: 'right',
                originY: 'top',
            });
            canvas.add(bgRect);

            // 4. Draw text (right-aligned inside background)
            pinText.set({
                left: rightX - totalWidth + paddingX,
                top: currentY + rowHeight / 2,
                originX: 'left',
                originY: 'center',
            });
            canvas.add(pinText);

            // 5. Draw icon (to the right of text, inside background)
            if (pin.blipIcon && pin.blipIcon.image) {
                await this.addBlipIcon(
                    canvas,
                    pin.blipIcon.image,
                    rightX - paddingX - (iconSize / 2), // icon center X
                    currentY + rowHeight / 2, // icon center Y
                    iconSize,
                    fabric
                );
            }

            currentY += rowHeight + gapY;
        }
    }

    async addBlipIcon(canvas, imageSrc, x, y, size, fabric) {
        return new Promise((resolve) => {
            fabric.Image.fromURL(imageSrc, (img) => {
                img.scaleToWidth(size);
                img.scaleToHeight(size);
                img.set({
                    left: x,
                    top: y,
                    originX: 'center',
                    originY: 'center'
                });
                canvas.add(img);
                resolve();
            }, {
                crossOrigin: 'anonymous'
            });
        });
    }

    async addPinMarkers(canvas, width, height, fabric) {
        const pinsData = window.markers || [];
        const map = window.map;

        console.log('Adding pin markers - pins count:', pinsData.length);
        if (!map || pinsData.length === 0) return;

        const mapContainer = map.getContainer();
        const mapWidth = mapContainer.offsetWidth;
        const mapHeight = mapContainer.offsetHeight;

        // Pin size similar to your current logic
        const baseSize = 48;
        const exportScale = Math.max(width / mapWidth, height / mapHeight);
        const pinSize = exportScale > 3 ? baseSize * 1.5 : baseSize;

        const usingIOSCrop = !!this.__iosCrop;
        const crop = this.__iosCrop;

        for (const pin of pinsData) {
            // Pixel position on the live map canvas
            const pt = map.project([pin.lngLat.lng, pin.lngLat.lat]);

            let x, y;
            if (usingIOSCrop) {
                // Convert CSS pixel coords (pt.x/pt.y) -> source canvas pixels (backing-store)
                // On iPhone, the captured image size equals the Mapbox canvas backing store,
                // which is mapCanvas.width/height = mapContainer.offsetWidth/Height * devicePixelRatio
                const mapCanvas = map.getCanvas();

                // Scale from CSS px to source px
                const cssToSrcX = mapCanvas.width  / mapWidth;
                const cssToSrcY = mapCanvas.height / mapHeight;

                const srcX = pt.x * cssToSrcX;
                const srcY = pt.y * cssToSrcY;

                // Now apply the iOS crop and scale into the export canvas
                const scaleX = width  / crop.sWidth;
                const scaleY = height / crop.sHeight;

                x = (srcX - crop.sx) * scaleX;
                y = (srcY - crop.sy) * scaleY;
                } else {
                // Non-iOS path (unchanged)
                const scaleX = width  / mapWidth;
                const scaleY = height / mapHeight;
                x = pt.x * scaleX;
                y = pt.y * scaleY;
            }

            if (pin.blipIcon && pin.blipIcon.image) {
                await this.addBlipIcon(canvas, pin.blipIcon.image, x, y, pinSize, fabric);
            }
        }
    }

    // Generate map configuration data for storage
    generateMapConfig() {
        const pinDataForExport = window.markers?.map(m => ({ 
            name: m.name, 
            lng: m.lngLat.lng, 
            lat: m.lngLat.lat,
            blipCode: m.blipIcon ? m.blipIcon.code : null
        })) || [];

        console.log('Export - Current markers array length:', window.markers?.length || 0);
        console.log('Export - Pin data for export:', pinDataForExport);

        return {
            title: document.getElementById('map-title')?.value || '',
            subtitle: document.getElementById('map-subtitle')?.value || '',
            pins: pinDataForExport,
            mapBounds: window.map?.getBounds() || null,
            mapCenter: window.map?.getCenter() || null,
            mapZoom: window.map?.getZoom() || 10,
            titleSettings: {
                position: document.getElementById('title-position')?.value || 'top-left',
                font: document.getElementById('title-font')?.value || 'Font 1',
                fontSize: document.getElementById('title-font-size')?.value || '3',
                borderSize: document.getElementById('title-border-size')?.value || '3',
                shadowBlur: document.getElementById('title-shadow-blur')?.value || '15'
            },
            subtitleSettings: {
                position: document.getElementById('subtitle-position')?.value || 'below-title',
                font: document.getElementById('subtitle-font')?.value || 'Font 2',
                fontSize: document.getElementById('subtitle-font-size')?.value || '1.5',
                borderSize: document.getElementById('subtitle-border-size')?.value || '2',
                shadowBlur: document.getElementById('subtitle-shadow-blur')?.value || '10'
            },
            timestamp: new Date().toISOString()
        };
    }

    async uploadToSupabase() {
    try {
        this.showLoading(true, 'Uploading to cloud...');
        const designId = `design_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

        // const fullResBlob = await this.exportPoster({
        //     width: 3600,
        //     height: 2250,
        //     scale: 1
        // });

        const { width, height } = (window.currentOrientation === 'portrait')
        ? { width: 2700, height: 3600 }   // Portrait
        : { width: 3600, height: 2250 };  // Landscape

        const fullResBlob = await this.exportPoster({ width, height, scale: 1 });


        const mapConfig = this.generateMapConfig();

        // 1. Upload high-resolution PNG
        const fullResPath = `fullres/${designId}_fullres.png`;
        const fullResUrl = await uploadImage(fullResBlob, fullResPath);
        if (!fullResUrl) throw new Error('Failed to upload full resolution image');

        // 2. Convert PNG blob to low-quality JPG
        const lowJpgBlob = await convertPngToJpg(fullResBlob);

        // 3. Upload low-quality JPG
        const lowJpgPath = `lowres/${designId}_lowres.jpg`;
        const lowJpgUrl = await uploadImage(lowJpgBlob, lowJpgPath);
        if (!lowJpgUrl) throw new Error('Failed to upload low resolution JPG image');

        // 4. Save design to Supabase database
        const designData = await saveDesign(fullResUrl, mapConfig);
        this.showLoading(false);

        // 5. Handle Shopify embed or product page redirect
        if (window.parent !== window) {
            window.parent.postMessage({
                type: 'GTA_DESIGN_COMPLETE',
                designId: designId,
                mapConfig: mapConfig,
                fullresImage: fullResUrl,
                createdAt: new Date().toISOString()
            }, '*');

            this.showShopifySuccess(designId);
        } else {
            const urlParams = new URLSearchParams(window.location.search);
            const productId = urlParams.get('product_id');

            if (productId) {
                await this.addToCartWithDesign(productId, designId, mapConfig, fullResUrl, lowJpgUrl);
            } else {
                this.showSuccess(fullResUrl, designData, lowJpgUrl);

                setTimeout(async () => {
                    const form = document.getElementById("gta-map-form");
                    if (form) {
                      form.addEventListener('submit', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }, { once: true });
                    } else {
                      return;
                    }

                    const formData = new FormData(form);
                    const payload = {
                        id: formData.get("id"),
                        quantity: parseInt(formData.get("quantity")) || 1,
                        properties: {
                            "_Image Design": fullResUrl,
                            "_Low quality design": lowJpgUrl
                        }
                    };

                    try {
                        const res = await fetch("/cart/add.js", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json"
                            },
                            body: JSON.stringify(payload)
                        });

                        if (!res.ok) {
                            const errData = await res.json();
                            console.error("Add to cart failed:", errData);
                            return;
                        }

                        try {
                            const headerRes = await fetch("/?section_id=header");
                            const headerHTML = await headerRes.text();
                            const headerDOM = new DOMParser().parseFromString(headerHTML, "text/html");
                            const newBubble = headerDOM.querySelector("#cart-icon-bubble");
                            const currentBubble = document.querySelector("#cart-icon-bubble");
                            if (newBubble && currentBubble) {
                                currentBubble.innerHTML = newBubble.innerHTML;
                            }
                        } catch (err) {
                            console.warn("Failed to update cart bubble:", err);
                        }

                        const drawerRes = await fetch("/?section_id=cart-drawer");
                        const drawerHTML = await drawerRes.text();
                        const drawerDOM = new DOMParser().parseFromString(drawerHTML, "text/html");
                        const newDrawer = drawerDOM.querySelector(".cart-drawer--desktop-width-normal.cart-drawer--mobile-width-full");
                        const currentDrawer = document.querySelector(".cart-drawer--desktop-width-normal.cart-drawer--mobile-width-full");

                        if (newDrawer && currentDrawer) {
                            currentDrawer.innerHTML = newDrawer.innerHTML;
                            currentDrawer.classList.remove("is-empty");
                            currentDrawer.classList.add("active");
                            document.body.classList.add("overflow-hidden");
                            // document.body.style.overflow = "hidden";
                            currentDrawer.scrollTo?.(0, 0);
                        }

                    } catch (err) {
                        console.error("Cart drawer error:", err);
                    }

                }, 100);
            }
        }

        return {
            fullResUrl,
            lowJpgUrl,
            mapConfig,
            designData
        };

    } catch (error) {
        console.error('Upload error:', error);
        this.showLoading(false);
        this.showError('Failed to upload design. Please try again.');
        throw error;
    }
}


    async downloadPoster() {
        try {
            // Show loading indicator
            this.showLoading(true, 'Generating poster...');
            
            // Export poster at high resolution
            // const blob = await this.exportPoster({
            //     width: 3600,
            //     height: 2250,
            //     scale: 1
            // });

            const { width, height } = (window.currentOrientation === 'portrait')
            ? { width: 2700, height: 3600 }
            : { width: 3600, height: 2250 };

            const blob = await this.exportPoster({ width, height, scale: 1 });

            
            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `GTA-Map-Poster-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            this.showLoading(false);
            return true;
            
        } catch (error) {
            console.error('Download error:', error);
            this.showLoading(false);
            throw error;
        }
    }

    showLoading(show, message = '') {
        const existingLoader = document.getElementById('export-loading');
        
        if (show && !existingLoader) {
            const loadingEl = document.createElement('div');
            loadingEl.id = 'export-loading';
            loadingEl.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background-color: rgba(0, 0, 0, 0.7);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10000;
              `;
              loadingEl.innerHTML = `
                <div class="circle-loader"></div>
                <style>
                    .circle-loader {
                        display: block !important;
                        border: 6px solid rgba(255, 255, 255, 0.2);
                        border-top: 6px solid #fff;
                        border-radius: 50%;
                        width: 60px;
                        height: 60px;
                        animation: spin 1s linear infinite;
                    }
    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                </style>
            `;
            document.body.appendChild(loadingEl);
        } else if (!show && existingLoader) {
            existingLoader.remove();
        }
    }

    showSuccess(fullResUrl, designData, lowJpgUrl = null) {
    const successEl = document.createElement('div');
    successEl.id = 'poster-image';

    // Generate input fields
    const inputs = `
        <input type="hidden" name="properties[_Image Design]" value="${fullResUrl}">
        ${lowJpgUrl ? `<input type="hidden" name="properties[_Low quality design]" value="${lowJpgUrl}">` : ''}
    `;

    successEl.innerHTML = inputs;
    const form = document.getElementById("gta-map-form");
    if (form) {
        form.prepend(successEl);
    }
}
  
    showShopifySuccess(designId) {
        const successEl = document.createElement('div');
        successEl.id = 'shopify-success';
        successEl.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: rgba(0, 0, 0, 0.95);
            color: white;
            padding: 30px 40px;
            border-radius: 8px;
            z-index: 10000;
            text-align: center;
            box-shadow: 0 4px 20px rgba(0, 255, 0, 0.3);
            border: 2px solid #00ff00;
            max-width: 500px;
            width: 90%;
        `;
        successEl.innerHTML = `
            <div style="font-family: 'Pricedown', Arial;">
                <h3 style="margin-bottom: 20px; font-size: 24px; color: #00ff00;">🛒 READY TO ADD TO CART!</h3>
                <p style="margin-bottom: 15px;">Your custom GTA map poster has been created!</p>
                <div style="margin: 20px 0; padding: 15px; background: rgba(0, 255, 0, 0.1); border-radius: 4px;">
                    <p style="margin-bottom: 10px; font-size: 14px;">✅ High-resolution image generated</p>
                    <p style="margin-bottom: 10px; font-size: 14px;">✅ Design saved to cloud</p>
                    <p style="font-size: 14px;">🚀 Taking you to the product page...</p>
                </div>
                <div style="margin-top: 20px;">
                    <div style="width: 200px; height: 6px; background: #333; border-radius: 3px; overflow: hidden; margin: 0 auto;">
                        <div style="width: 100%; height: 100%; background: #00ff00; 
                            animation: progress 2s linear;"></div>
                    </div>
                </div>
            </div>
            <style>
                @keyframes progress {
                    from { width: 0%; }
                    to { width: 100%; }
                }
            </style>
        `;
        document.body.appendChild(successEl);
        
        // Auto-remove after animation completes
        setTimeout(() => {
            if (successEl.parentNode) {
                successEl.remove();
            }
        }, 2500);
    }

    showError(message) {
        const errorEl = document.createElement('div');
        errorEl.id = 'upload-error';
        errorEl.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: rgba(0, 0, 0, 0.95);
            color: white;
            padding: 30px 40px;
            border-radius: 8px;
            z-index: 10000;
            text-align: center;
            box-shadow: 0 4px 20px rgba(255, 0, 0, 0.3);
            border: 2px solid #ff0000;
            max-width: 400px;
            width: 90%;
        `;
        errorEl.innerHTML = `
            <div style="font-family: 'Pricedown', Arial;">
                <h3 style="margin-bottom: 20px; font-size: 24px; color: #ff0000;">UPLOAD FAILED</h3>
                <p style="margin-bottom: 20px;">${message}</p>
                <button onclick="this.parentElement.parentElement.remove()" style="
                    background: #ff0000;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-weight: bold;
                ">CLOSE</button>
            </div>
        `;
        document.body.appendChild(errorEl);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (errorEl.parentNode) {
                errorEl.remove();
            }
        }, 5000);
    }

    getShopifyStoreUrl() {
        // First try to get the store URL from Shopify's global window.shopUrl
        if (window.shopUrl) {
            return window.shopUrl;
        }

        // Get the current URL
        const url = new URL(window.location.href);

        // If we're on localhost, try to get the store URL from the referrer
        if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
            // Try to get store URL from referrer
            if (document.referrer) {
                const referrerUrl = new URL(document.referrer);
                // Check if referrer is a Shopify domain
                if (referrerUrl.hostname.includes('myshopify.com') || referrerUrl.hostname === 'posterlabz.com') {
                    return referrerUrl.origin;
                }
            }
            
            // If no referrer, check URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const shop = urlParams.get('shop');
            if (shop) {
                return `https://${shop}`;
            }

            // Fallback to primary domain if in development
            console.warn('⚠️ Using fallback Shopify store URL in development mode');
            return 'https://posterlabz.com';
        }

        // For all other cases, use the current origin
        return url.origin;
    }

    async addToCartWithDesign(productId, designId, mapConfig, fullResUrl) {
        try {
            // Prevent any other form submissions
            const form = document.querySelector('[data-type="add-to-cart-form"]');
            if (form) {
                form.addEventListener('submit', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }, { capture: true });
            }

            console.log('🛒 Adding product to cart with custom design:', { productId, designId });
            console.log('🌐 Current URL:', window.location.href);
            console.log('📋 URL Parameters:', window.location.search);
            
            // Show loading for add to cart
            this.showLoading(true, 'ADDING TO CART...');
            
            // Get URL parameters for variant selection
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get('variant_id');
            const quantity = parseInt(urlParams.get('quantity') || '1');
            
            console.log('🔍 Parsed parameters:', { 
                productId, 
                variantId, 
                quantity,
                allParams: Object.fromEntries(urlParams)
            });
            
            if (!variantId) {
                throw new Error('No variant ID specified. Please provide variant_id in the URL.');
            }
            
            console.log('🛒 Using variant ID:', variantId);
            
            // Create unique cart identifier
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(2, 15);
            const uniqueCartId = `${timestamp}_${random}_${designId.substring(0, 8)}`;
            
            // Create design summary
            const designSummary = this.createDesignSummary(mapConfig, designId);
            
            // Prepare cart add data (using items array format)
            const cartData = {
                items: [{
                    id: parseInt(variantId),
                    quantity: quantity,
                    properties: {
                        'FullResImage': fullResUrl,
                        'MapConfig': JSON.stringify(mapConfig),
                        'PosterSize': urlParams.get('poster_size') || '18x24',
                        'PosterType': urlParams.get('poster_type') || 'Digital Download',
                        'DesignID': designId,
                        'CreatedAt': new Date().toISOString(),
                        '_CartID': uniqueCartId,
                        'CustomDesign': designSummary,
                        '_VariantID': `${designId}_${timestamp}`
                    }
                }]
            };
            
            console.log('🛒 Cart data prepared:', cartData);
            
            // Get Shopify store URL from product URL or use a configured store URL
            const shopifyStoreUrl = this.getShopifyStoreUrl();
            const cartApiUrl = `${shopifyStoreUrl}/cart/add.js`;
            
            console.log('🛒 Cart API URL:', cartApiUrl);
            
            // Add to cart using Shopify's cart API
            const response = await fetch(cartApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(cartData)
            });
            
            const responseText = await response.text();
            console.log('🛒 Cart API response:', responseText);
            
            if (!response.ok) {
                console.error('❌ Cart API error status:', response.status);
                console.error('❌ Cart API error response:', responseText);
                throw new Error(`Failed to add to cart: ${response.status} - ${responseText}`);
            }
            
            const result = JSON.parse(responseText);
            console.log('✅ Successfully added to cart:', result);
            
            this.showLoading(false);
            this.showCartSuccess(designId, fullResUrl);
            
            // Redirect to cart after showing success
            setTimeout(() => {
                console.log('🚀 Redirecting to cart...');
                window.location.href = '/cart';
            }, 3000);
            
        } catch (error) {
            console.error('❌ Failed to add to cart:', error);
            this.showLoading(false);
            
            // Try alternative approach - redirect back to product page with design data
            console.log('🔄 Trying alternative approach - redirecting to product page...');
            const productUrl = `/products/${productId}?design_id=${designId}&fullres_image=${encodeURIComponent(fullResUrl)}&map_config=${encodeURIComponent(JSON.stringify(mapConfig))}`;
            
            this.showError(`Cart API failed. Redirecting to product page... ${error.message}`);
            
            setTimeout(() => {
                window.location.href = productUrl;
            }, 2000);
        }
    }

    createDesignSummary(mapConfig, designId) {
        try {
            const config = typeof mapConfig === 'string' ? JSON.parse(mapConfig) : mapConfig;
            const pinCount = config.pins ? config.pins.length : 0;
            const title = config.title || config.titleSettings?.text || 'Custom Map';
            return `${title} (${pinCount} pins) - ${designId.substring(0, 8)}`;
        } catch (error) {
            return `Custom GTA Map Design - ${designId.substring(0, 8)}`;
        }
    }

    showCartSuccess(designId, fullResUrl) {
        const successEl = document.createElement('div');
        successEl.id = 'cart-success';
        successEl.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: rgba(0, 0, 0, 0.95);
            color: white;
            padding: 30px 40px;
            border-radius: 8px;
            z-index: 10000;
            text-align: center;
            box-shadow: 0 4px 20px rgba(0, 255, 0, 0.3);
            border: 2px solid #00ff00;
            max-width: 500px;
            width: 90%;
        `;
        successEl.innerHTML = `
            <div style="font-family: 'Pricedown', Arial;">
                <h3 style="margin-bottom: 20px; font-size: 24px; color: #00ff00;">🛒 ADDED TO CART!</h3>
                <p style="margin-bottom: 15px;">Your custom GTA map poster has been added to your cart.</p>
                <div style="margin: 20px 0; padding: 15px; background: rgba(0, 255, 0, 0.1); border-radius: 4px;">
                    <img src="${fullResUrl}" alt="Custom Design" style="width: 100%; max-width: 200px; border: 1px solid #00ff00; margin-bottom: 10px;">
                    <p style="margin: 5px 0; font-size: 14px;">✅ High-resolution design saved</p>
                    <p style="margin: 5px 0; font-size: 14px;">🆔 Design ID: ${designId.substring(0, 12)}</p>
                </div>
                <p style="font-size: 14px; margin-bottom: 15px;">🚀 Redirecting to cart...</p>
                <div style="display: flex; gap: 10px; justify-content: center;">
                    <button onclick="window.location.href='/cart'" style="
                        background: #00ff00;
                        color: black;
                        border: none;
                        padding: 10px 15px;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: bold;
                    ">🛒 View Cart</button>
                    <button onclick="window.location.href='/pages/map-builder'" style="
                        background: transparent;
                        color: #00ff00;
                        border: 1px solid #00ff00;
                        padding: 10px 15px;
                        border-radius: 4px;
                        cursor: pointer;
                    ">🎨 Design Another</button>
                </div>
            </div>
        `;
        document.body.appendChild(successEl);
        
        // Auto-remove after 5 seconds if user doesn't click anything
        setTimeout(() => {
            if (successEl.parentNode) {
                successEl.remove();
            }
        }, 5000);
    }
}

// Test function for Supabase connectivity
window.testSupabaseConnection = async function() {
    try {
        console.log('Testing Supabase connection...');
        
        // Test storage bucket access
        const { data: buckets, error: bucketsError } = await supabaseClient.storage.listBuckets();
        if (bucketsError) {
            console.error('Buckets error:', bucketsError);
            return false;
        }
        console.log('✅ Storage buckets accessible:', buckets);
        
        // Test database table access
        const { data: testData, error: tableError } = await supabaseClient
            .from('map_designs')
            .select('id')
            .limit(1);
        
        if (tableError) {
            console.error('❌ Database error:', tableError);
            console.log('💡 You may need to set up Row Level Security policy. Run this SQL in Supabase:');
            console.log(`
-- Enable anonymous inserts for map_designs table
create policy "Allow anonymous inserts" on public.map_designs
for insert with check (true);

-- Allow anonymous reads (optional, for testing)
create policy "Allow anonymous reads" on public.map_designs
for select using (true);
            `);
            return false;
        }
        
        console.log('✅ Database table accessible');
        console.log('🎉 Supabase connection test successful!');
        return true;
        
    } catch (error) {
        console.error('❌ Supabase connection test failed:', error);
        return false;
    }
};

// Export for use in main.js
window.CanvasExporter = CanvasExporter;